import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast/Toast';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import InfoQ from '@/images/InfoQ.svg';
import { RequestStatus } from '@/pages/avatar/common/types';
import premiumPlusCreateAvatarStore from '@/pages/avatar/PremiumPlusCreatePage/CreateAvatarStore';
import { uploadFile } from '@/services/api/studioApi';
import theme from '@/theme';
import styled from '@emotion/styled';
import { tooltipClasses } from '@mui/material/Tooltip';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

const Container = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    border: solid;
    border-radius: 8px;
    border-width: 1px;
    border-color: ${theme.colors.black[300]};
    height: 52px;
    padding: 16px 20px 16px 20px;
    margin-top: 8px;
    margin-bottom: 16px;
`;

const UploadButton = styled.label`
    background: none;
    border: none;
    color: ${theme.colors.primary[900]};
    cursor: pointer;
    font-size: 16px;
    padding: 0;
    text-decoration: underline;

    &:hover {
        color: ${theme.colors.primary[700]};
    }
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: ${theme.colors.error[500]};
    cursor: pointer;
    font-size: 16px;
    padding: 0;
    text-decoration: underline;

    &:hover {
        color: ${theme.colors.error[700]};
    }
`;

const StyledToolTip = styled(CustomTooltip)`
    & .${tooltipClasses.tooltip} {
        background-color: ${theme.colors.secondary.main};
    }
    font-size: 0;
`;

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${theme.colors.grey[500]};
`;

const ToolTipText = () => {
    return <span>Upload a video to be shown when the avatar is idle.</span>;
};

export const IdleVideoUpload = observer(() => {
    const [fileName, setFileName] = useState<string | null>(null);
    const isUploading = premiumPlusCreateAvatarStore.idleVideoUploadStatus === RequestStatus.LOADING;

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        premiumPlusCreateAvatarStore.idleVideoUploadStatus = RequestStatus.LOADING;
        setFileName(file.name);
        try {
            const sourceUrl = await uploadFile(file, undefined, { s3Uri: true });
            premiumPlusCreateAvatarStore.setIdleVideo(sourceUrl);
            premiumPlusCreateAvatarStore.idleVideoUploadStatus = RequestStatus.IDLE;
        } catch (error) {
            toast.error({ title: 'Failed to upload idle video' });
            setFileName(null);
            premiumPlusCreateAvatarStore.idleVideoUploadStatus = RequestStatus.IDLE;
        }
    };

    const handleRemove = () => {
        premiumPlusCreateAvatarStore.setIdleVideo(undefined);
        setFileName(null);
        premiumPlusCreateAvatarStore.idleVideoUploadStatus = RequestStatus.IDLE;
    };

    return (
        <Container>
            {premiumPlusCreateAvatarStore.idleVideo ? (
                <>
                    {fileName ? `Idle video uploaded: ${fileName}` : 'Idle video uploaded'}
                    <RemoveButton onClick={handleRemove}>Remove</RemoveButton>
                </>
            ) : (
                <>
                    <UploadButton htmlFor="idle-video-upload">{isUploading ? 'Uploading...' : 'Upload idle video'}</UploadButton>
                    <input
                        id="idle-video-upload"
                        type="file"
                        accept="video/*"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        disabled={isUploading}
                    />
                </>
            )}
            <StyledToolTip cursor="pointer" title={<ToolTipText />} placement="top">
                <StyledMaskedIcon src={InfoQ} size="16px" />
            </StyledToolTip>
        </Container>
    );
});
